import type { FulfillmentAdminClient, InventoryRow } from '@/infrastructure/FulfillmentAdminClient';

/** One sku's stock on the fulfillment book, as the scan desk shows it beside an order line (SSD-7521). */
export interface SkuStock {
  onHand: number;
  reserved: number;
  available: number;
}

/**
 * Each sku summed across every shelf it sits on, the way Current Stocks and the inbound request form sum it:
 * on hand is every unit on the book, reserved is what orders hold on shelves, available is on hand less reserved.
 */
export const stockBySku = (rows: readonly InventoryRow[]): Record<string, SkuStock> => {
  const totals: Record<string, { onHand: number; reserved: number }> = {};
  for (const row of rows) {
    const total = (totals[row.sku] ??= { onHand: 0, reserved: 0 });
    total.onHand += row.onHand;
    total.reserved += row.reserved;
  }
  return Object.fromEntries(
    Object.entries(totals).map(([sku, total]) => [
      sku,
      { onHand: total.onHand, reserved: total.reserved, available: total.onHand - total.reserved },
    ]),
  );
};

/** The distinct skus of an order's lines, sorted, so the same order reads the same key. */
export const orderSkus = (lines: ReadonlyArray<{ sku: string | null | undefined }>): string[] =>
  [...new Set(lines.map((line) => (line.sku ?? '').trim()).filter((sku) => sku !== ''))].sort();

/** 500 is the server's page cap; a read still going after this many pages is not a read to trust. */
const INVENTORY_PAGE_ROWS = 500;
const INVENTORY_MAX_PAGES = 50;

/**
 * The stock of these skus, every shelf row read to the end of the list. Zero shelves come too, so a sku now at 0 still
 * shows and a reservation held on an empty shelf still counts against available. A read that fails or never ends
 * rejects: a part of the shelves added up would look exactly like the whole.
 */
export const readSkuStock = async (
  client: Pick<FulfillmentAdminClient, 'listInventory'>,
  skus: string[],
): Promise<Record<string, SkuStock>> => {
  const rows: InventoryRow[] = [];
  const visited = new Set<string>();
  let cursor: string | undefined;
  for (let page = 0; page < INVENTORY_MAX_PAGES; page += 1) {
    const result = await client.listInventory({
      skus,
      includeEmpty: true,
      limit: INVENTORY_PAGE_ROWS,
      ...(cursor ? { cursor } : {}),
    });
    rows.push(...(result.rows ?? []));
    const next = result.nextCursor;
    if (!next) {
      return stockBySku(rows);
    }
    if (visited.has(next)) {
      break;
    }
    visited.add(next);
    cursor = next;
  }
  throw new Error('The inventory read did not reach its last page');
};
